import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ME_KEY } from '../auth/session';
import { api } from './client';
import type { Invitation, JoinInfo, Me, VoicePart } from './types';

/** A member's part and singer roles; a section leader leads the section of their part. */
export interface SingerRoles {
  part: VoicePart;
  chorist: boolean;
  sectionLeader: boolean;
}

export interface InvitationRequest extends SingerRoles {
  displayName: string;
  email: string;
}

/** The link the director sends: the joining page for the invitation's token. */
export function invitationLink(token: string): string {
  return `${window.location.origin}/join/${token}`;
}

/** Director: the invitations neither used nor revoked (expired ones included). */
export function useInvitations(choirId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['invitations', choirId],
    queryFn: () => api<Invitation[]>(`/api/choirs/${choirId}/invitations`),
    enabled: !!choirId && enabled,
  });
}

export function useCreateInvitation(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: InvitationRequest) =>
      api<Invitation>(`/api/choirs/${choirId}/invitations`, { method: 'POST', json: { ...request, email: request.email.trim() || null } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invitations', choirId] }),
  });
}

/** "New link": the old link stops working. */
export function useRenewInvitation(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<Invitation>(`/api/choirs/${choirId}/invitations/${id}/renew`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invitations', choirId] }),
  });
}

export function useRevokeInvitation(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/choirs/${choirId}/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invitations', choirId] }),
  });
}

export function useChangeMember(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...roles }: SingerRoles & { userId: string }) =>
      api<void>(`/api/choirs/${choirId}/members/${userId}`, { method: 'PATCH', json: roles }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members', choirId] }),
  });
}

export function useRemoveMember(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api<void>(`/api/choirs/${choirId}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['members', choirId] }),
  });
}

/** The choir's reference pitch; the membership in /api/me carries it, so that is reloaded. */
export function useChangeReferencePitch(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (referencePitchHz: number) => api<void>(`/api/choirs/${choirId}`, { method: 'PATCH', json: { referencePitchHz } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ME_KEY }),
  });
}

/** The joining page: what the invitation is for, or where it stands. */
export function useJoinInfo(token: string | undefined) {
  return useQuery({
    queryKey: ['join', token],
    queryFn: () => api<JoinInfo>(`/api/invitations/${token}`),
    enabled: !!token,
    retry: false,
  });
}

export interface NewAccount {
  displayName: string;
  email: string;
  password: string;
  privacyNoticeRead: boolean;
  locale: string;
}

/** Someone new: the account is created, joins the choir and is signed in. */
export function useJoinWithNewAccount(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (account: NewAccount) => api<Me>(`/api/invitations/${token}/join-new`, { method: 'POST', json: account }),
    onSuccess: (me) => {
      queryClient.clear();
      queryClient.setQueryData(ME_KEY, me);
    },
  });
}

/** Someone signed in: the choir is added to their account. */
export function useJoinWithCurrentAccount(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (privacyNoticeRead: boolean) => api<Me>(`/api/invitations/${token}/join`, { method: 'POST', json: { privacyNoticeRead } }),
    onSuccess: (me) => queryClient.setQueryData(ME_KEY, me),
  });
}
